import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle, XCircle, Mail, Loader2 } from 'lucide-react';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import { verifyEmail, resendVerification } from '../services/auth';
import { useAuthStore } from '../store/authStore';
import { useTutorialStore } from '../store/tutorialStore';
import { getErrorMessage } from '../services/api';

export default function VerifyEmail() {
  const { t } = useTranslation('auth');
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const emailParam = searchParams.get('email') || '';

  const [email, setEmail] = useState(emailParam);
  const [isVerifying, setIsVerifying] = useState(!!token);
  const [isResending, setIsResending] = useState(false);
  const [verificationSuccess, setVerificationSuccess] = useState(false);
  const [verificationError, setVerificationError] = useState('');
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendError, setResendError] = useState('');

  const navigate = useNavigate();
  const setAuth = useAuthStore((state) => state.setAuth);
  const setNewUser = useTutorialStore((state) => state.setNewUser);

  // Vérifier le token automatiquement si présent
  useEffect(() => {
    if (token) {
      handleVerifyEmail();
    }
  }, [token]);

  const handleVerifyEmail = async () => {
    if (!token) return;

    setIsVerifying(true);
    setVerificationError('');

    try {
      const response = await verifyEmail({ token });

      if (response.success && response.data) {
        setVerificationSuccess(true);

        // Connecter l'utilisateur automatiquement
        setAuth(response.data.user, {
          accessToken: response.data.accessToken,
          refreshToken: response.data.refreshToken,
          expiresIn: response.data.expiresIn,
        });

        // Marquer comme nouvel utilisateur pour déclencher le tutoriel
        setNewUser(true);

        // Rediriger vers le dashboard après 2 secondes
        setTimeout(() => {
          navigate('/dashboard');
        }, 2000);
      }
    } catch (err) {
      setVerificationError(getErrorMessage(err));
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResendVerification = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) {
      setResendError(t('verifyEmail.enterEmailError'));
      return;
    }

    setIsResending(true);
    setResendError('');
    setResendSuccess(false);

    try {
      const response = await resendVerification({ email });

      if (response.success) {
        setResendSuccess(true);
      }
    } catch (err) {
      setResendError(getErrorMessage(err));
    } finally {
      setIsResending(false);
    }
  };

  // Écran de vérification en cours
  if (isVerifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4 py-12">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <Link to="/" className="text-3xl font-bold text-primary-600">
              Splinity
            </Link>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 text-center">
            <Loader2 className="w-16 h-16 text-primary-600 animate-spin mx-auto mb-6" />
            <h1 className="text-2xl font-bold text-gray-900 mb-4">
              {t('verifyEmail.verifying')}
            </h1>
            <p className="text-gray-600">
              {t('verifyEmail.verifyingMessage')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Écran de succès
  if (verificationSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4 py-12">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <Link to="/" className="text-3xl font-bold text-primary-600">
              Splinity
            </Link>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>

            <h1 className="text-2xl font-bold text-gray-900 mb-4">
              {t('verifyEmail.successTitle')}
            </h1>

            <p className="text-gray-600 mb-6">
              {t('verifyEmail.successMessage')}
            </p>

            <Button
              variant="primary"
              className="w-full"
              onClick={() => navigate('/dashboard')}
            >
              {t('verifyEmail.goToDashboard')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Écran d'erreur de vérification
  if (verificationError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4 py-12">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <Link to="/" className="text-3xl font-bold text-primary-600">
              Splinity
            </Link>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-8 h-8 text-red-600" />
            </div>

            <h1 className="text-2xl font-bold text-gray-900 mb-4">
              {t('verifyEmail.errorTitle')}
            </h1>

            <p className="text-gray-600 mb-6">
              {verificationError}
            </p>

            <div className="space-y-4">
              <p className="text-sm text-gray-500">
                {t('verifyEmail.resendPrompt')}
              </p>

              <form onSubmit={handleResendVerification} className="space-y-4">
                <Input
                  label={t('login.email')}
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('login.emailPlaceholder')}
                  required
                />

                {resendError && (
                  <p className="text-sm text-red-600">{resendError}</p>
                )}

                {resendSuccess && (
                  <p className="text-sm text-green-600">
                    {t('verifyEmail.resendSuccess')}
                  </p>
                )}

                <Button
                  type="submit"
                  variant="primary"
                  className="w-full"
                  isLoading={isResending}
                >
                  {t('verifyEmail.resendButton')}
                </Button>
              </form>

              <Button
                variant="outline"
                className="w-full"
                onClick={() => navigate('/login')}
              >
                {t('backToLogin', { ns: 'common' })}
              </Button>
            </div>
          </div>

          <p className="mt-8 text-center text-sm text-gray-500">
            <Link to="/" className="hover:text-primary-600">
              &larr; {t('backToHome', { ns: 'common' })}
            </Link>
          </p>
        </div>
      </div>
    );
  }

  // Écran de renvoi d'email (pas de token dans l'URL)
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4 py-12">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <Link to="/" className="text-3xl font-bold text-primary-600">
            Splinity
          </Link>
          <h1 className="mt-6 text-2xl font-bold text-gray-900">
            {t('verifyEmail.title')}
          </h1>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8">
          <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Mail className="w-8 h-8 text-primary-600" />
          </div>

          <p className="text-gray-600 text-center mb-6">
            {t('verifyEmail.enterEmail')}
          </p>

          <form onSubmit={handleResendVerification} className="space-y-4">
            <Input
              label={t('login.email')}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('login.emailPlaceholder')}
              required
            />

            {resendError && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {resendError}
              </div>
            )}

            {resendSuccess && (
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
                {t('verifyEmail.resendSuccessLong')}
              </div>
            )}

            <Button
              type="submit"
              variant="primary"
              className="w-full"
              size="lg"
              isLoading={isResending}
            >
              {t('verifyEmail.sendVerificationLink')}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <Link to="/login" className="text-sm text-primary-600 hover:underline">
              {t('backToLogin', { ns: 'common' })}
            </Link>
          </div>
        </div>

        <p className="mt-8 text-center text-sm text-gray-500">
          <Link to="/" className="hover:text-primary-600">
            &larr; {t('backToHome', { ns: 'common' })}
          </Link>
        </p>
      </div>
    </div>
  );
}
